import { useMemo, useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type {
  MyBrandKpiCards as MyBrandKpiCardsData,
  MyBrandKpiCharts as MyBrandKpiChartsData,
  CompetitorKpiCards as CompetitorKpiCardsData,
  CompetitorKpiMetric,
} from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { industriesQueryOptions } from "@/lib/queries/opportunity-sets.queries";
import {
  dashboardSummaryQueryOptions,
  dashboardMyBrandChartQueryOptions,
  dashboardCompetitorSummaryQueryOptions,
  type MyBrandDashboardFilters,
  type CompetitorDashboardFilters,
} from "@/lib/queries/dashboard.queries";
import { resolveDashboardRequestWindow } from "../../../lib/resolve-dashboard-request-window";
import { useDashboardFiltersStore } from "../../../stores/dashboardFilters.store";
import { useDashboardAsyncResource } from "../../../hooks/useDashboardAsyncResource";
import {
  MyBrandKpiCards,
  type MyBrandKpiCardsState,
} from "../../MyBrandKpiCards/MyBrandKpiCards";
import { MyBrandKpiChart } from "../../MyBrandKpiChart/MyBrandKpiChart";
import {
  CompetitorKpiCards,
  type CompetitorKpiCardsState,
} from "../../CompetitorKpiCards/CompetitorKpiCards";
import { CompetitorKpiTrendDrawer } from "../../CompetitorKpiTrendDrawer/CompetitorKpiTrendDrawer";
import { DashboardOpportunitySetsSection } from "../../DashboardOpportunitySetsSection/DashboardOpportunitySetsSection";
import { deriveCardState } from "../../../lib/card-state";
import { buildMyBrandCardViews } from "../../../lib/metrics";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";

export function DashboardPage() {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  // Drive the KPI request off the *applied* snapshot — the header edits a draft
  // that only commits here once the user clicks Apply.
  const {
    mode,
    appliedCompetitorFilters,
    appliedMyBrandFilters,
    selectedCards: selectedCardsArr,
    northStar,
    toggleCard,
  } = useDashboardFiltersStore();

  const selectedCards = useMemo(() => new Set(selectedCardsArr), [selectedCardsArr]);
  const { metricById } = useKpiSelectorCatalog("overview");

  const { data: industries = [], isLoading: industriesLoading } = useQuery(
    industriesQueryOptions("dashboard", activeTenantId),
  );

  const myBrandWindow = resolveDashboardRequestWindow(appliedMyBrandFilters);
  const competitorWindow = resolveDashboardRequestWindow(appliedCompetitorFilters);

  // Normalize the store's My Brand filters for the API contract (arrays only):
  //   platforms null (All) → []  — param omitted by query builder, backend returns all rows
  //   products  null (All) → []  — param omitted, backend returns all rows
  //   products  []   (none) → ['__no_match__'] — param present but never matches, backend returns 0 rows
  //   [product_code values] → pass through
  const myBrandFiltersTyped: MyBrandDashboardFilters = {
    platforms: appliedMyBrandFilters.platforms ?? [],
    accounts: appliedMyBrandFilters.accounts ?? [],
    products:
      appliedMyBrandFilters.products === null
        ? []
        : appliedMyBrandFilters.products.length === 0
          ? ["__no_match__"]
          : appliedMyBrandFilters.products,
    dateRange: appliedMyBrandFilters.dateRange as MyBrandDashboardFilters["dateRange"],
    startDate: myBrandWindow.startDate,
    endDate: myBrandWindow.endDate,
    activePresetId: appliedMyBrandFilters.activePresetId,
  };
  const competitorFiltersTyped: CompetitorDashboardFilters = {
    myIndustry: appliedCompetitorFilters.myIndustry,
    myBrand: appliedCompetitorFilters.myBrand,
    industryProducts: appliedCompetitorFilters.industryProducts,
    competitorsIndustry: appliedCompetitorFilters.competitorsIndustry,
    mainCompetitor: appliedCompetitorFilters.mainCompetitor,
    benchmark: appliedCompetitorFilters.benchmark,
    platforms: appliedCompetitorFilters.platforms,
    dateRange: appliedCompetitorFilters.dateRange as CompetitorDashboardFilters["dateRange"],
    startDate: competitorWindow.startDate,
    endDate: competitorWindow.endDate,
    activePresetId: appliedCompetitorFilters.activePresetId,
  };

  const filtersHash =
    mode === "my-brand"
      ? JSON.stringify(myBrandFiltersTyped)
      : JSON.stringify(competitorFiltersTyped);
  const resetKey = `${activeTenantId ?? ""}:${mode}:${filtersHash}`;

  // My Brand cards and chart load as two independent async jobs so the cards can
  // render before the (heavier) chart data arrives. Competitors stay a single job.
  const myBrandSummaryOptions = dashboardSummaryQueryOptions(
    mode === "my-brand" ? activeTenantId : null,
    myBrandFiltersTyped,
  );
  const myBrandChartOptions = dashboardMyBrandChartQueryOptions(
    mode === "my-brand" ? activeTenantId : null,
    myBrandFiltersTyped,
  );
  const competitorSummaryOptions = dashboardCompetitorSummaryQueryOptions(
    mode === "competitors" ? activeTenantId : null,
    competitorFiltersTyped,
  );

  const { data: myBrandSummary } = useQuery(myBrandSummaryOptions);
  const { data: myBrandChart } = useQuery(myBrandChartOptions);
  const { data: competitorSummary } = useQuery(competitorSummaryOptions);

  const myBrandCardsResource = useDashboardAsyncResource<MyBrandKpiCardsData>({
    summary: myBrandSummary,
    targetKey: myBrandSummaryOptions.queryKey,
    resultField: "kpiCards",
    resetKey,
    enabled: mode === "my-brand" && !!activeTenantId,
  });

  const myBrandChartResource = useDashboardAsyncResource<MyBrandKpiChartsData>({
    summary: myBrandChart,
    targetKey: myBrandChartOptions.queryKey,
    resultField: "charts",
    resetKey,
    enabled: mode === "my-brand" && !!activeTenantId,
  });

  const competitorResource = useDashboardAsyncResource<CompetitorKpiCardsData>({
    summary: competitorSummary,
    targetKey: competitorSummaryOptions.queryKey,
    resultField: "kpiCards",
    resetKey,
    enabled: mode === "competitors" && !!activeTenantId,
  });

  const myBrandKpiCards = myBrandCardsResource.data;
  const myBrandCharts = myBrandChartResource.data;
  const competitorKpiCards = competitorResource.data;

  const myBrandCardState = deriveCardState(
    myBrandCardsResource.timedOut,
    myBrandCardsResource.status,
    myBrandKpiCards,
  ) as MyBrandKpiCardsState;

  const myBrandChartState = deriveCardState(
    myBrandChartResource.timedOut,
    myBrandChartResource.status,
    myBrandCharts,
  ) as MyBrandKpiCardsState;

  const competitorCardState = deriveCardState(
    competitorResource.timedOut,
    competitorResource.status,
    competitorKpiCards,
  ) as CompetitorKpiCardsState;

  const [selectedCompetitorMetric, setSelectedCompetitorMetric] =
    useState<CompetitorKpiMetric | null>(null);
  const [isTrendDrawerOpen, setIsTrendDrawerOpen] = useState(false);

  useEffect(() => {
    setIsTrendDrawerOpen(false);
    setSelectedCompetitorMetric(null);
  }, [filtersHash]);

  const handleCompetitorCardClick = useCallback((metric: CompetitorKpiMetric) => {
    setSelectedCompetitorMetric(metric);
    setIsTrendDrawerOpen(true);
  }, []);

  const selectedMetricLabel = useMemo(() => {
    if (!selectedCompetitorMetric || !competitorKpiCards) return "";
    return competitorKpiCards[selectedCompetitorMetric]?.label ?? "";
  }, [selectedCompetitorMetric, competitorKpiCards]);

  const myBrandCardViews = useMemo(
    () =>
      myBrandKpiCards
        ? buildMyBrandCardViews(myBrandKpiCards, northStar, metricById)
        : undefined,
    [myBrandKpiCards, northStar, metricById],
  );

  const industryLabelByIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const industry of industries) {
      map.set(industry.index, industry.title);
    }
    return map;
  }, [industries]);

  if (!industriesLoading && industries.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
          <AlertTriangle className="h-7 w-7 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-foreground">
            No industries found
          </h2>
          <p className="text-sm text-muted-foreground">
            Please contact administration to set up industry data for your
            account.
          </p>
        </div>
      </div>
    );
  }

  if (mode === "my-brand") {
    return (
      <div className="flex flex-1 flex-col gap-6 p-6">
        <MyBrandKpiCards
          state={myBrandCardState}
          cards={myBrandCardViews}
          activePresetId={appliedMyBrandFilters.activePresetId}
          selectedCards={selectedCards}
          onCardToggle={toggleCard}
        />
        <MyBrandKpiChart
          state={myBrandChartState}
          charts={myBrandCharts ?? undefined}
          northStar={northStar}
          selectedCards={selectedCards}
          window={myBrandWindow}
          catalogById={metricById}
        />
        <DashboardOpportunitySetsSection
          tenantId={activeTenantId}
          industryLabelByIndex={industryLabelByIndex}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <CompetitorKpiCards
        state={competitorCardState}
        data={competitorKpiCards ?? undefined}
        animationKey={filtersHash}
        selectedMetric={selectedCompetitorMetric}
        onCardClick={handleCompetitorCardClick}
      />
      <CompetitorKpiTrendDrawer
        tenantId={activeTenantId}
        open={isTrendDrawerOpen}
        metric={selectedCompetitorMetric}
        metricLabel={selectedMetricLabel}
        filters={competitorFiltersTyped}
        filtersHash={filtersHash}
        onOpenChange={setIsTrendDrawerOpen}
      />
      <DashboardOpportunitySetsSection
        tenantId={activeTenantId}
        industryLabelByIndex={industryLabelByIndex}
      />
    </div>
  );
}
